import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, RefreshCw, Leaf, TrendingUp, LogOut } from "lucide-react";
import { fetchLeads, updateLeadStatus, getToken, clearToken } from "../lib/api";
import { LOGO_URL } from "../lib/content";

const STATUSES = ["new", "contacted", "won", "lost"];
const statusColor = {
  new: "bg-gold/20 text-gold border-gold/40",
  contacted: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  won: "bg-green-500/20 text-green-300 border-green-500/40",
  lost: "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const logout = () => {
    clearToken();
    toast.success("Signed out.");
    navigate("/login");
  };

  const load = async () => {
    if (!getToken()) {
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const { data } = await fetchLeads();
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        clearToken();
        navigate("/login");
        return;
      }
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: leads.filter((l) => l.status === s).length }), {});

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-gold/20 bg-black/60 sticky top-0 z-10 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="logo" className="h-11 w-auto" />
          </a>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400 hidden sm:block">Lead Dashboard</span>
            <button data-testid="refresh-leads" onClick={load} className="flex items-center gap-2 text-gold border border-gold/40 px-4 py-2 rounded-sm hover:bg-gold/10 transition-colors text-sm">
              <RefreshCw size={15} /> Refresh
            </button>
            <button data-testid="logout-button" onClick={logout} className="flex items-center gap-2 text-gray-300 border border-gray-600 px-4 py-2 rounded-sm hover:border-red-500 hover:text-red-400 transition-colors text-sm">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#111111] border border-gold/20 rounded-xl p-5">
            <div className="flex items-center gap-2 text-gold"><TrendingUp size={18} /><span className="text-xs uppercase tracking-widest">Total</span></div>
            <p className="font-serif text-3xl font-bold mt-2">{leads.length}</p>
          </div>
          {STATUSES.map((s) => (
            <div key={s} className="bg-[#111111] border border-gold/20 rounded-xl p-5">
              <span className="text-xs uppercase tracking-widest text-gray-400 capitalize">{s}</span>
              <p className="font-serif text-3xl font-bold mt-2">{counts[s]}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", ...STATUSES].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-sm text-xs uppercase tracking-widest border transition-colors ${
                filter === f ? "bg-gold text-black border-gold" : "border-gold/30 text-gray-300 hover:border-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-gray-400 text-center py-20">Loading leads...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-gold/20 rounded-xl">
            <Leaf className="text-gold/40 mx-auto" size={40} />
            <p className="text-gray-400 mt-4">No leads {filter !== "all" && `with status "${filter}"`} yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {filtered.map((l) => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="bg-[#111111] border border-gold/20 rounded-xl p-6 hover:border-gold/40 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif text-xl text-white">{l.name}</h3>
                      <span className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full border ${statusColor[l.status] || statusColor.new}`}>{l.status}</span>
                      <span className="text-xs px-3 py-1 rounded-full bg-gold/10 text-gold border border-gold/30">{l.service}</span>
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-gray-300">
                      <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:text-gold"><Phone size={14} className="text-gold" /> {l.phone}</a>
                      {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-gold break-all"><Mail size={14} className="text-gold" /> {l.email}</a>}
                      {l.address && <span className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> {l.address}</span>}
                    </div>
                    {l.message && <p className="mt-3 text-sm text-gray-400 italic">"{l.message}"</p>}
                    <p className="mt-3 text-xs text-gray-600">{new Date(l.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex md:flex-col gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        data-testid={`set-status-${s}-${l.id}`}
                        onClick={() => changeStatus(l.id, s)}
                        disabled={l.status === s}
                        className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors capitalize ${
                          l.status === s ? "opacity-40 cursor-default border-gold/20" : "border-gold/40 text-gray-300 hover:bg-gold hover:text-black"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
